// src/app/modules/Devices/components/ScansTab.jsx
// What each fingerprint scan turned into, plus the review queue for scans that
// couldn't be applied automatically (unverified clock, out of order, unknown finger).

import React, { useState } from 'react';
import { ScanLine, CheckCircle, XCircle, WifiOff, Clock } from 'lucide-react';
import { toast } from 'react-toastify';
import Moment from 'moment';
import { devicesApi, errorMessage } from '../api';
import { usePolling, useEvent, dateTime, OUTCOMES, OUTCOME_DETAILS, REVIEW_REASONS } from '../utils';
import { Pill, EmptyState, Button, DvModal, Spinner } from './ui';

const REFRESH_MS = 5000;

function ResolveModal({ event, orgId, onHide, onResolved }) {
  const [time, setTime] = useState(() => Moment(event.device_time).format('YYYY-MM-DDTHH:mm'));
  const [members, setMembers] = useState(null);
  const [membershipId, setMembershipId] = useState('');
  const [busy, setBusy] = useState(null);
  const needsMember = !event.membership_id;

  React.useEffect(() => {
    if (needsMember) devicesApi.enrollableMembers(orgId).then((r) => setMembers(r.members)).catch(() => setMembers([]));
  }, [needsMember, orgId]);

  const submit = async (action) => {
    setBusy(action);
    try {
      await devicesApi.resolveEvent(orgId, event.device_event_id, action === 'apply'
        ? { action, effective_time: new Date(time).toISOString(), ...(needsMember && { membership_id: membershipId }) }
        : { action });
      toast.success(action === 'apply' ? 'Scan applied to attendance' : 'Scan dismissed');
      onResolved();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <DvModal
      show
      onHide={onHide}
      title="Review scan"
      subtitle={`Fingerprint #${event.uid} · ${event.display_name || 'unknown person'}`}
      footer={(
        <>
          <Button variant="ghost" onClick={onHide}>Cancel</Button>
          <Button variant="danger" icon={XCircle} loading={busy === 'dismiss'} onClick={() => submit('dismiss')}>Dismiss</Button>
          <Button variant="primary" icon={CheckCircle} loading={busy === 'apply'} disabled={needsMember && !membershipId} onClick={() => submit('apply')}>Apply</Button>
        </>
      )}
    >
      <div className="dv-notice dv-notice--warning">
        <Clock size={16} />
        <span>{REVIEW_REASONS[event.review?.reason] || 'This scan needs a decision.'}</span>
      </div>
      <dl className="dv-facts" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div className="dv-fact"><dt>Device time</dt><dd>{dateTime(event.device_time)}</dd></div>
        <div className="dv-fact"><dt>Received</dt><dd>{dateTime(event.received_at)}</dd></div>
      </dl>
      {needsMember && (
        <label className="dv-field">
          <span>Who was it?</span>
          <select value={membershipId} onChange={(e) => setMembershipId(e.target.value)}>
            <option value="">Select a person…</option>
            {(members || []).map((m) => <option key={m.membership_id} value={m.membership_id}>{m.display_name}</option>)}
          </select>
          <small>This applies the scan only. To make this fingerprint work in future, re-enrol the person.</small>
        </label>
      )}
      <label className="dv-field">
        <span>Time to record</span>
        <input type="datetime-local" value={time} onChange={(e) => setTime(e.target.value)} />
        <small>Correct it if the device clock was wrong. Applying checks the person in or out at this time.</small>
      </label>
    </DvModal>
  );
}

export function ScansTab({ orgId, device, canManage, onChanged }) {
  const [filter, setFilter] = useState('all'); // all | review
  const [rows, setRows] = useState(null);
  const [reviewing, setReviewing] = useState(null);

  const load = useEvent(async () => {
    try {
      const res = await devicesApi.events(orgId, {
        device_id: device.device_id, limit: 100, ...(filter === 'review' && { review_status: 'pending' }),
      });
      setRows(res.events);
    } catch (err) {
      if (rows === null) setRows([]);
    }
  });
  usePolling(load, REFRESH_MS);
  const mounted = React.useRef(false);
  React.useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; } // initial load comes from usePolling
    setRows(null);
    load();
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="dv-card__head" style={{ borderBottom: 0, paddingBottom: 0 }}>
        <div className="dv-toolbar">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter scans">
            <option value="all">All scans</option>
            <option value="review">Needs review</option>
          </select>
        </div>
      </div>

      {rows === null ? (
        <div className="dv-card__body" style={{ textAlign: 'center' }}><Spinner /></div>
      ) : rows.length === 0 ? (
        <EmptyState icon={ScanLine} title={filter === 'review' ? 'Nothing to review' : 'No scans yet'}>
          {filter === 'review' ? 'Every scan was applied automatically.' : 'Scans appear here as soon as the device sends them.'}
        </EmptyState>
      ) : (
        <div className="dv-table-wrap">
          <table className="dv-table">
            <thead>
              <tr><th>Scan time</th><th>Person</th><th>Result</th>{canManage && <th />}</tr>
            </thead>
            <tbody>
              {rows.map((ev) => {
                const o = OUTCOMES[ev.outcome] || { label: ev.outcome, tone: 'neutral' };
                const detail = OUTCOME_DETAILS[ev.outcome_detail] || (ev.review?.status === 'pending' && REVIEW_REASONS[ev.review.reason]);
                return (
                  <tr key={ev.device_event_id}>
                    <td>
                      <div className="dv-strong">{dateTime(ev.device_time)}</div>
                      <div className="dv-sub" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {ev.offline && <><WifiOff size={11} /> sent later</>}
                        {!ev.time_trusted && <span>· clock unverified</span>}
                      </div>
                    </td>
                    <td>
                      <div className="dv-strong">{ev.display_name || 'Unknown'}</div>
                      <div className="dv-sub">Fingerprint #{ev.uid}</div>
                    </td>
                    <td>
                      <Pill tone={o.tone}>{o.label}</Pill>
                      {detail && <div className="dv-sub">{detail}</div>}
                      {ev.review?.status === 'resolved' && <div className="dv-sub">Reviewed · {ev.review.resolution}</div>}
                    </td>
                    {canManage && (
                      <td className="dv-actions">
                        {ev.review?.status === 'pending' && <Button size="sm" variant="primary" onClick={() => setReviewing(ev)}>Review</Button>}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {reviewing && (
        <ResolveModal
          event={reviewing}
          orgId={orgId}
          onHide={() => setReviewing(null)}
          onResolved={() => { setReviewing(null); load(); if (onChanged) onChanged(); }}
        />
      )}
    </>
  );
}

export default ScansTab;
